/**
 * FilterSortBar — search, filter, and sort controls for the active shelf.
 */

import React from 'react';
import { Search, SlidersHorizontal, ArrowDownUp, X, Star } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Checkbox } from '@/components/ui/checkbox';
import { cx } from '@/components/site/primitives';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useBookshelf } from './context/BookshelfContext';
import { GENRES, SORT_OPTIONS, RATING_OPTIONS } from './constants';
import { BOOKSHELF } from '@/constants/testIds';

const STATUS_OPTIONS = [
  { value: 'tbr', label: 'To Be Read' },
  { value: 'reading', label: 'Currently Reading' },
  { value: 'finished', label: 'Finished' },
];

function FilterFields({ filters, updateGenre, updateStatus, setRating }) {
  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-subtle">Status</p>
        <div className="mt-2.5 space-y-2">
          {STATUS_OPTIONS.map(opt => (
            <label key={opt.value} className="flex items-center gap-2.5 text-sm text-ink">
              <Checkbox
                checked={filters.status.includes(opt.value)}
                onCheckedChange={(checked) => updateStatus(opt.value, checked)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-subtle">Minimum Rating</p>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {RATING_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setRating(filters.rating === opt.value ? null : opt.value)}
              className={cx(
                'flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                filters.rating === opt.value
                  ? 'border-coral bg-coral/10 text-coral'
                  : 'border-line text-subtle hover:border-coral/40 hover:text-ink'
              )}
            >
              {opt.value}
              <Star className="h-3 w-3 fill-yellow text-yellow" />
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-subtle">Genre</p>
        <div className="mt-2.5 grid max-h-48 grid-cols-2 gap-x-3 gap-y-2 overflow-y-auto pr-1">
          {GENRES.map(genre => (
            <label key={genre} className="flex items-center gap-2 text-sm text-ink">
              <Checkbox
                checked={filters.genres.includes(genre)}
                onCheckedChange={(checked) => updateGenre(genre, checked)}
              />
              <span className="truncate">{genre}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function SortFields({ sortBy, setSortBy }) {
  return (
    <div className="space-y-1">
      {SORT_OPTIONS.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => setSortBy(opt.value)}
          className={cx(
            'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors',
            sortBy === opt.value ? 'bg-coral/10 font-semibold text-coral' : 'text-ink hover:bg-cream'
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function FilterSortBar() {
  const { filters, setFilters, sortBy, setSortBy } = useBookshelf();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const activeFilterCount =
    filters.genres.length + filters.status.length + (filters.rating ? 1 : 0) + (filters.isFavoriteOnly ? 1 : 0);

  const updateGenre = (genre, checked) =>
    setFilters(prev => ({ ...prev, genres: checked ? [...prev.genres, genre] : prev.genres.filter(g => g !== genre) }));
  const updateStatus = (status, checked) =>
    setFilters(prev => ({ ...prev, status: checked ? [...prev.status, status] : prev.status.filter(s => s !== status) }));
  const setRating = (rating) => setFilters(prev => ({ ...prev, rating }));
  const clearAll = () => setFilters({ genres: [], status: [], rating: null, isFavoriteOnly: false, searchQuery: filters.searchQuery });

  const filterProps = { filters, updateGenre, updateStatus, setRating };

  return (
    <div className="u-container mt-6 flex items-center gap-2.5" data-testid={BOOKSHELF.filterBar}>
      <div className="relative flex-1">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" />
        <input
          type="text"
          placeholder="Search by title, author, or genre…"
          value={filters.searchQuery}
          onChange={e => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
          className="w-full rounded-full border border-line bg-card py-2.5 pl-10 pr-4 text-sm text-ink outline-none transition-colors placeholder:text-subtle focus:border-coral"
        />
      </div>

      {isDesktop ? (
        <>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cx(
                  'relative flex items-center gap-1.5 rounded-full border px-3.5 py-2.5 text-sm font-medium transition-colors',
                  activeFilterCount > 0 ? 'border-coral text-coral' : 'border-line text-ink hover:border-coral/40'
                )}
              >
                <SlidersHorizontal className="h-4 w-4" /> Filter
                {activeFilterCount > 0 && (
                  <span className="grid h-4 w-4 place-items-center rounded-full bg-coral text-[10px] font-semibold text-[#FFF7F0]">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 rounded-2xl border-line bg-card p-4 text-ink shadow-xl">
              <div className="flex items-center justify-between">
                <p className="font-display text-base font-semibold">Filters</p>
                {activeFilterCount > 0 && (
                  <button type="button" onClick={clearAll} className="text-xs font-medium text-coral hover:underline">
                    Clear all
                  </button>
                )}
              </div>
              <div className="mt-3">
                <FilterFields {...filterProps} />
              </div>
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2.5 text-sm font-medium text-ink transition-colors hover:border-coral/40">
                <ArrowDownUp className="h-4 w-4" /> Sort
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 rounded-2xl border-line bg-card p-2 text-ink shadow-xl">
              <SortFields sortBy={sortBy} setSortBy={setSortBy} />
            </PopoverContent>
          </Popover>
        </>
      ) : (
        <Sheet>
          <SheetTrigger asChild>
            <button
              type="button"
              className={cx(
                'relative flex items-center gap-1.5 rounded-full border px-3.5 py-2.5 text-sm font-medium',
                activeFilterCount > 0 ? 'border-coral text-coral' : 'border-line text-ink'
              )}
              aria-label="Filter and sort"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {activeFilterCount > 0 && (
                <span className="absolute -right-1 -top-1 grid h-4 w-4 place-items-center rounded-full bg-coral text-[10px] font-semibold text-[#FFF7F0]">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-3xl border-line bg-card p-5 pb-8 text-ink">
            <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-line" />
            <div className="flex items-center justify-between">
              <SheetTitle className="font-display text-lg font-semibold text-ink">Filter & Sort</SheetTitle>
              {activeFilterCount > 0 && (
                <button type="button" onClick={clearAll} className="flex items-center gap-1 text-xs font-medium text-coral">
                  <X className="h-3 w-3" /> Clear all
                </button>
              )}
            </div>

            <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-subtle">Sort by</p>
            <div className="mt-2">
              <SortFields sortBy={sortBy} setSortBy={setSortBy} />
            </div>

            <div className="mt-5 border-t border-line pt-5">
              <FilterFields {...filterProps} />
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
